'use client';

/**
 * Load the Google Maps Places script once, on demand, from any client
 * component — public pages included (the key endpoint is public; the key is
 * NEXT_PUBLIC_* and referrer-restricted on Google's side).
 *
 * Extracted from AddressAutocomplete 2026-07-11 because the instant-quote
 * AddressInput polled window.google without anything ever loading the script,
 * leaving its input permanently disabled on /instant-quote.
 */
let scriptPromise: Promise<void> | null = null;

export function loadMapsScript(): Promise<void> {
  if (window.google?.maps?.places) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = fetch('/api/config/maps-key')
    .then(r => r.json())
    .then(({ key }: { key: string }) => {
      if (!key) throw new Error('No Google Maps key');
      return new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
        s.async = true;
        // Resolving on onload (not on Places availability) is the contract:
        // callers poll window.google.maps.places because the library can
        // attach after the tag finishes loading.
        s.onload = () => {
          resolve();
          if (!window.google?.maps?.places) {
            // The script tag finished but the Places library never attached
            // (library blocked, key rejected after load, bootstrap error).
            // Keeping the cached promise would make a later Retry reuse the
            // already-resolved promise and never add a fresh script tag
            // (#5321). Clear it so the next call starts a new load; callers
            // whose poll finds Places shortly after onload are unaffected
            // because the check above short-circuits them.
            scriptPromise = null;
          }
        };
        s.onerror = () => reject(new Error('Failed to load Google Maps'));
        document.head.appendChild(s);
      });
    })
    .catch(() => {
      scriptPromise = null; // allow retry
    });

  return scriptPromise;
}
